import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { setMagicToken } from '@/utils/auth';

import { ZEALOT_AUTH_MESSAGES } from './constants';
import type { AuthTokenMessage, EmbedStrategy } from './types';

type UseEmbedMessagesOptions = {
  strategy: EmbedStrategy;
  onAuthComplete?: () => void;
  onAuthCancel?: () => void;
};

// gloss
export const useEmbedMessages = ({ strategy, onAuthComplete, onAuthCancel }: UseEmbedMessagesOptions) => {
  const router = useRouter();
  const onAuthCompleteRef = useRef(onAuthComplete);
  const onAuthCancelRef = useRef(onAuthCancel);

  useEffect(() => {
    onAuthCompleteRef.current = onAuthComplete;
    onAuthCancelRef.current = onAuthCancel;
  }, [onAuthComplete, onAuthCancel]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (strategy.parentOrigin !== '*' && event.origin !== strategy.parentOrigin) {
        return;
      }

      const data = event.data;
      if (!data?.type) return;

      switch (data.type) {
        case ZEALOT_AUTH_MESSAGES.AUTH_TOKEN: {
          const tokenMsg = data as AuthTokenMessage;

          if (tokenMsg.token) {
            setMagicToken(tokenMsg.token);
            onAuthCompleteRef.current?.();
          }

          if (tokenMsg.email) {
            const currentEmail = router.query.email;
            if (currentEmail !== tokenMsg.email) {
              router.replace(
                { pathname: router.pathname, query: { ...router.query, email: tokenMsg.email } },
                undefined,
                { shallow: true },
              );
            }
            if (!tokenMsg.token) {
              onAuthCompleteRef.current?.();
            }
          }
          break;
        }
        case ZEALOT_AUTH_MESSAGES.AUTH_CANCEL: {
          onAuthCancelRef.current?.();
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [strategy.parentOrigin, router]);

  const requestAuth = (brandId?: string) => {
    window.parent.postMessage({ type: ZEALOT_AUTH_MESSAGES.AUTH_REQUIRED, brandId }, strategy.parentOrigin);
  };

  return { requestAuth };
};
